import InputField from "components/controls/InputField";
import SelectField from "components/controls/SelectField";
import { useFormikContext } from "formik";
import { ROUTES } from "library/constants";
import { ECheckedValue, SelectItem } from "library/types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useGetBenefitCategories } from "state/queries/useGetBenefitCategories";

type Props = {
  isAddCategory?: boolean;
  countryCode?: number | string;
  clientInternalNumberGOS?: number | string;
  rootCountryCode?: boolean;
};

export function BenefitCategoryNameField({
  isAddCategory,
  countryCode = 0,
  clientInternalNumberGOS = 0,
  rootCountryCode = true,
}: Props) {
  const { t } = useTranslation();
  const location = useLocation();

  const isCompanyProfileRoute = location.pathname === ROUTES.COMPANY_PROFILE;

  const routesWithSelectCategory = [
    ROUTES.COUNTRY,
    ROUTES.COMPANY_PROFILE,
  ].includes(location.pathname);

  // While adding on COUNTRY/COMPANY_PROFILE pick an existing category via the Select.
  // While editing the name is shown in an InputField:
  //  - COUNTRY: editable
  //  - COMPANY_PROFILE: disabled (name not editable here)
  const hasCategorySelect = Boolean(isAddCategory) && routesWithSelectCategory;
  const isNameDisabled = !isAddCategory && isCompanyProfileRoute;

  const { setValues, values, setFieldValue, errors } =
    useFormikContext<FormData>();

  const [selectedCategory, setSelectedCategory] = useState<SelectItem | null>(
    null,
  );

  const { data: rootBenefitCategories } = useGetBenefitCategories({
    countryCode: rootCountryCode ? 0 : countryCode,
    clientInternalNumberGOS: 0,
    enabled: hasCategorySelect,
  });

  const { data: countryCategories } = useGetBenefitCategories({
    countryCode,
    clientInternalNumberGOS,
    enabled: hasCategorySelect,
  });

  const categoryOptions = useMemo(() => {
    return (rootBenefitCategories?.benefitCategories || [])
      ?.filter((cat) => {
        return !countryCategories?.benefitCategories?.some(
          (countryCat) =>
            countryCat.benefitCategoryParentTag === cat.benefitCategoryTag ||
            countryCat.benefitCategoryTag === cat.benefitCategoryTag,
        );
      })
      ?.map((category) => ({
        value: category?.benefitCategoryTag || "",
        text: category?.name || "",
        ...category,
      }));
  }, [rootBenefitCategories, countryCategories]);

  if (!hasCategorySelect) {
    return (
      <InputField
        label={t("hbh.name.label")}
        name="name"
        value={values["name"]}
        setFieldValue={setFieldValue}
        error={errors["name"]}
        disabled={isNameDisabled}
        required
      />
    );
  }

  return (
    <>
      <SelectField
        required
        showEmptyOption
        error={""}
        setFieldValue={(name, benefitCategory) => {
          setSelectedCategory(benefitCategory);
          setValues({
            benefitCategory: benefitCategory?.value,

            name: benefitCategory?.name || "",

            consentsNeeded: benefitCategory?.consentsNeeded
              ? ECheckedValue.Yes
              : ECheckedValue.No,

            isAutoEnrollment: benefitCategory?.isAutoEnrollment
              ? ECheckedValue.Yes
              : ECheckedValue.No,
            coverPhoto: benefitCategory?.backgroundPicturePath
              ? {
                  name: benefitCategory?.backgroundPictureName,
                  path: benefitCategory?.backgroundPicturePath,
                  base64String: benefitCategory?.backgroundPictureContent,
                  type: benefitCategory?.backgroundPictureMimeType,
                }
              : null,
            description: benefitCategory?.description || "",
            benefitCategoryParentTag: benefitCategory?.benefitCategoryTag,
          } as any);
        }}
        value={selectedCategory as any}
        label={t("hbh.benefitCategory.label")}
        options={categoryOptions}
        name={"benefitCategory"}
      />
    </>
  );
}
